import { createCompany } from '../models/companyModels';
import {
  createUserAddress,
  createRoute,
  createWatchlistStatistics,
} from '../models/watchlistModels';
import { createRouteAnalysis } from '../models/routeAnalysisModels';

const routeIdOf = (route) => route?.routeId ?? route?.route_id;

const mapCompanySummary = (summary) => {
  if (!summary || typeof summary !== 'object') return null;
  return createCompany({
    ticker: summary.ticker,
    name: summary.name,
    industry: summary.industry,
    tier: summary.tier,
    overall_risk_score: summary.overall_risk_score,
    risk_level: summary.risk_level,
    has_risk_analysis: summary.has_risk_analysis,
    updated_at: summary.updated_at,
  });
};

const mapRouteAnalyses = (raw) => {
  if (!raw || typeof raw !== 'object') return {};
  const mapped = {};
  Object.entries(raw).forEach(([routeId, analysis]) => {
    if (analysis) {
      mapped[routeId] = createRouteAnalysis(analysis);
    }
  });
  return mapped;
};

const mapReadModelItem = (item) => {
  const companyId = item.company_id ?? item.companyId ?? '';
  const outboundRoutes = (item.routes ?? []).map((route) => createRoute(route));
  const inboundRoutes = (item.inbound_routes ?? []).map(({ source_company_id, route }) => {
    const mapped = createRoute(route);
    return {
      ...(mapped.toJSON ? mapped.toJSON() : mapped),
      route_id: mapped.routeId ?? route?.route_id,
      routeId: mapped.routeId ?? route?.route_id,
      _ownerCompanyId: source_company_id ?? route?.source_company_id,
    };
  });
  const userAddresses = (item.addresses ?? []).map((addr) => createUserAddress(addr));
  const companyType = String(item.company_type ?? item.companyType ?? '')
    .trim()
    .toLowerCase();

  return {
    companyId,
    company_id: companyId,
    addedDate: item.added_date ?? item.addedDate,
    notes: item.notes ?? '',
    alertEnabled: item.alert_enabled ?? item.alertEnabled ?? false,
    priority: item.priority ?? 'normal',
    portStartPoint: item.port_start_point ?? item.portStartPoint ?? '',
    portEndPoint: item.port_end_point ?? item.portEndPoint ?? '',
    company: mapCompanySummary(item.company_summary ?? item.companySummary),
    routeAnalyses: mapRouteAnalyses(item.route_analyses ?? item.routeAnalyses),
    userCompanyInfo: {
      companyType,
      company_type: companyType,
      userAddresses,
      routes: [...outboundRoutes, ...inboundRoutes],
    },
  };
};

export const mapReadModelToWatchlistItems = (items = []) =>
  (Array.isArray(items) ? items : []).map(mapReadModelItem);

export const mapReadModelStatistics = (statistics) => {
  if (!statistics) return null;
  return createWatchlistStatistics(statistics);
};

export const collectEmbeddedRouteAnalyses = (watchlistItems = []) => {
  const analyses = {};
  watchlistItems.forEach((item) => {
    Object.entries(item.routeAnalyses ?? {}).forEach(([routeId, analysis]) => {
      analyses[routeId] = analysis;
    });
  });
  return analyses;
};

export const routesNeedingAnalysis = (watchlistItems = []) => {
  const refs = [];
  watchlistItems.forEach((item) => {
    const companyId = item.companyId ?? item.company_id;
    const embedded = item.routeAnalyses ?? {};
    (item.userCompanyInfo?.routes ?? []).forEach((route) => {
      const routeId = routeIdOf(route);
      const ownerId = route._ownerCompanyId ?? companyId;
      if (!routeId || !ownerId) return;
      if (!embedded[routeId]) {
        refs.push({
          routeId,
          route_id: routeId,
          company_id: ownerId,
          companyId: ownerId,
          _ownerCompanyId: ownerId,
        });
      }
    });
  });
  return refs;
};

export const companiesFromWatchlistItems = (watchlistItems = []) =>
  watchlistItems
    .map((item) => item.company)
    .filter(Boolean);
